import React from 'react';
import { withRouter } from 'react-router-dom';
import { Container } from 'reactstrap';
import './CrewTimesheet.scss';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import { CircularProgress } from '@mui/material';
import JobDetailHeader from './JobDetailHeader';
import TimesheetTimeAndPhaseCodes from './TimesheetTimeAndPhaseCodes';
import TimesheetDetailRow from './TimesheetDetailRow';
import _ from 'lodash-es';
import { toasty } from '../common/forms/FormElements';
import CrewTimesheetManagementRow from './CrewTimesheetManagementRow';
import { AppNavPaths } from '../AppNavPaths';
import CrewTimesheetStatusRow from './CrewTimesheetStatusRow';
import { TimesheetStatus } from '../timesheet/Timesheet';
import { getTenantUserProfile } from '../common/TenantUserProfile';
import { hasAnyAdjustmentData } from './CrewTimesheet';
import UnconfirmedAssignmentsAlert from './UnconfirmedAssignmentsAlert';
import AdjustmentDetail from './AdjustmentDetail';

class CrewTimesheetForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = { loading: true, isAdmin: false, canBeAdmin: false };

        this.onSaveClick = this.onSaveClick.bind(this);
        this.onSubmitClick = this.onSubmitClick.bind(this);
        this.onAcceptClick = this.onAcceptClick.bind(this);
        this.onRejectClick = this.onRejectClick.bind(this);

        this.onTimesheetMutated = this.onTimesheetMutated.bind(this);
    }

    componentDidMount() {
        this.populateState(true);
    }

    getIsExported = (timesheet) =>
        Boolean(
            timesheet && timesheet.timesheetStatusId >= TimesheetStatus.Exported
        );

    getIsReadOnly = (timesheet) =>
        Boolean(
            timesheet &&
            timesheet.timesheetStatusId >= TimesheetStatus.Submitted
        ) || hasAnyAdjustmentData(timesheet);

    async populateState(fetchJobDetails) {
        const timesheetId = this.props.match.params.id;

        const [timesheet, job, arcGisLocation, userProfile, absenceReasons] =
            await Promise.all([
                util.fetch.js(ApiRoutes.crewTimesheet.get(timesheetId)),
                fetchJobDetails
                    ? util.fetch.js(
                          ApiRoutes.crewTimesheet.getJobDetails(timesheetId)
                      )
                    : {},
                util.fetch.js(
                    ApiRoutes.arcGisLocations.forTimesheet(timesheetId)
                ),
                getTenantUserProfile(),
                util.fetch.js(ApiRoutes.absenceReason.getAll()),
            ]);

        const { userPermissions } = userProfile;

        const isAdmin =
            userPermissions && userPermissions.includes('timesheet.accept');

        const isTimesheetApprover =
            userPermissions && userPermissions.includes('timesheet.approve');

        if (fetchJobDetails) {
            this.setState({ job });
        }

        this.setState({
            timesheetId,
            timesheet,
            loading: false,
            originalTimesheet: _.cloneDeep(timesheet),
            isExported: this.getIsExported(timesheet),
            isReadOnly: isAdmin || this.getIsReadOnly(timesheet),
            arcGisLocation,
            isAdmin,
            absenceReasons,
            isTimesheetApprover,
            modifyingUsers: timesheet.modifyingUsers,
        });
    }

    async onSaveClick() {
        this.setState({ loading: true });

        const { timesheet } = this.state;

        const saveResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.crewTimesheet.update(),
            timesheet
        );

        if (saveResponse) {
            await this.populateState();

            toasty.success('Timesheet saved', 'Your timesheet has been saved.');
        } else {
            this.setState({ loading: false });
        }
    }

    async onSubmitClick() {
        this.setState({ loading: true });

        const { timesheet } = this.state;

        const submitResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.crewTimesheet.submit(),
            timesheet
        );

        if (submitResponse) {
            await this.populateState();

            toasty.success(
                'Timesheet submitted',
                'Your timesheet has been submitted.'
            );
        } else {
            this.setState({ loading: false });
        }
    }

    async onAcceptClick() {
        this.setState({ loading: true });

        const { timesheetId, isTimesheetApprover } = this.state;

        const acceptResponse = await util.fetch.andGetResponse(
            util.fetch.types.put,
            ApiRoutes.timesheetManagement.accept(timesheetId)
        );

        const toastTitle = isTimesheetApprover
            ? 'Timesheet approved'
            : 'Timesheet validated';

        const toastMessage = isTimesheetApprover
            ? 'The timesheet has been marked as approved.'
            : 'The timesheet has been marked as validated.';

        if (acceptResponse) {
            toasty.success(toastTitle, toastMessage);

            this.props.history.push(AppNavPaths.TimesheetManagement);
        }

        this.setState({ loading: false });
    }

    async onRejectClick(dataContainer) {
        if (!dataContainer) {
            return;
        }

        const { rejectionNotes } = dataContainer;

        const { timesheetId, isTimesheetApprover } = this.state;

        const rejectResponse = await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.timesheetManagement.reject(),
            {
                timesheetId,
                note: rejectionNotes,
            }
        );

        const toastTitle = isTimesheetApprover
            ? 'Timesheet invalidated'
            : 'Timesheet rejected';

        const toastMessage = isTimesheetApprover
            ? 'The timesheet has been downgraded from validated to submitted.'
            : 'The timesheet has been marked as rejected.';

        if (rejectResponse) {
            toasty.success(toastTitle, toastMessage);

            this.props.history.push(AppNavPaths.TimesheetManagement);
        }

        this.setState({ loading: false });
    }

    onRecallRequested = async () => {
        this.setState({ loading: true });

        const { timesheetId, isAdmin } = this.state;

        if (!timesheetId || isAdmin) {
            return;
        }

        try {
            const timesheet = await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.crewTimesheet.recall(timesheetId),
                timesheetId
            );

            if (timesheet) {
                this.setState({
                    timesheet,
                    loading: false,
                    originalTimesheet: _.cloneDeep(timesheet),
                    isReadOnly: this.getIsReadOnly(timesheet),
                });
            } else {
                await this.populateState();
            }
        } catch (e) {
            console.error({ e });
            this.setState({ loading: false });
        }
    };

    onTimesheetMutated = (timesheet) => {
        this.setState({ timesheet });
    };

    onEnableAdminEditMode = () => {
        const { isAdmin } = this.state;

        if (!isAdmin) {
            return;
        }

        this.setState({ isReadOnly: false });
    };

    render() {
        const { loading, timesheet } = this.state;

        if (loading) {
            return (
                <CircularProgress color="secondary" variant="indeterminate" />
            );
        }

        return (
            <Container
                className="timesheet-container p-3 m-3 ml-auto mr-auto"
                fluid={true}
            >
                <JobDetailHeader
                    {...this.state}
                    onSaveClick={this.onSaveClick}
                    onSubmitClick={this.onSubmitClick}
                />

                <UnconfirmedAssignmentsAlert {...this.state} />

                <CrewTimesheetStatusRow
                    {...this.state}
                    onRecallRequested={this.onRecallRequested}
                />

                <AdjustmentDetail entity={timesheet} {...this.state} />

                <div className="timesheet-grid-shell">
                    <div className="timesheet-grid-scroll">
                        <TimesheetTimeAndPhaseCodes
                            {...this.state}
                            onTimesheetMutated={this.onTimesheetMutated}
                        />

                        {timesheet.timesheetDetails.map((td) => (
                            <div key={td.id} className="detail-row mb-2">
                                <TimesheetDetailRow
                                    timesheetDetail={td}
                                    {...this.state}
                                    onTimesheetMutated={this.onTimesheetMutated}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <CrewTimesheetManagementRow
                    {...this.state}
                    onAcceptClick={this.onAcceptClick}
                    onEnableAdminEditMode={this.onEnableAdminEditMode}
                    onRejectClick={this.onRejectClick}
                />
            </Container>
        );
    }
}

export default withRouter(CrewTimesheetForm);

